import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { useCreateChildExperience } from '@/features/experiences/hooks/useCreateChildExperience';
import { useGetExperienceChildConfigs } from '@/features/experiences/hooks/useGetExperienceChildConfigs';
import { useUpdateExperience } from '@/features/experiences/hooks/useUpdateExperience';
import { useActiveView } from '@/features/planner/hooks/useActiveView';
import { queryClient } from '@/lib';
import type { ExperienceLayouts } from '@/types/gql.generated';
import { createExperienceQueryKey } from '@/utils/queryKeys';
import { useAddViewGate } from '../useAddViewGate';
import { useCustomizeMode } from '../useCustomizeMode';

/**
 * Live state of the Add-a-view / Change-layout flow, derived from the URL and
 * consumed by CustomizeBar to render AddViewTopBar as a takeover of the top bar.
 * Replaces the former AddViewFlowContext: instead of the picker publishing this
 * upward, the header owns the confirm/cancel actions and reads the selection
 * (mode + layout) straight from the URL params.
 */
export type AddViewFlow = {
  mode: 'create' | 'change';
  parentTitle: string;
  canConfirm: boolean;
  isPending: boolean;
  confirm: () => void;
  cancel: () => void;
};

/**
 * Derives the active Add-a-view / Change-layout flow for the top bar. Runs at the
 * parent planner's scope (CustomizeBar sits above ChildExperienceLoader), so
 * change-mode confirm targets the active child explicitly via updateExperience's
 * experienceId override and invalidates the parent so the sidebar stays in sync.
 * Returns null when no picker is open.
 */
export const useAddViewFlow = (): AddViewFlow | null => {
  const { experience } = usePageExperienceContext();
  const { addViewMode, layout, closeAddView, closeChangeLayout, finishCreate } =
    useCustomizeMode();
  const { activeChildId } = useActiveView(experience);
  const { configs } = useGetExperienceChildConfigs({
    enabled: addViewMode === 'create',
  });
  const { createChildExperience, isPending } = useCreateChildExperience(
    experience.id
  );
  const { updateExperience } = useUpdateExperience();
  // Also gated at the entry points; repeated here because this is the step that
  // actually creates the view, and the picker can be reached directly by URL param.
  const { runGatedAddView } = useAddViewGate();

  if (addViewMode === null) return null;

  if (addViewMode === 'create') {
    const config = layout?.mode === 'create' ? layout.config : null;
    // A config lingering in the URL that the server no longer offers reads as
    // "nothing selected" — same existence contract as a source feed id.
    const canConfirm = config != null && configs.some((c) => c.name === config);
    return {
      mode: 'create',
      parentTitle: experience.title,
      canConfirm,
      isPending,
      confirm: () => {
        if (!canConfirm || !config) return;
        runGatedAddView(() =>
          createChildExperience(config, {
            onSuccess: ({ createChildExperience: { id } }) => finishCreate(id),
          })
        );
      },
      cancel: closeAddView,
    };
  }

  const layoutId: ExperienceLayouts | null =
    layout?.mode === 'change' ? layout.layoutId : null;
  return {
    mode: 'change',
    parentTitle: experience.title,
    canConfirm: layoutId != null,
    // Change is fire-and-forget (optimistic), so the confirm button never spins.
    isPending: false,
    confirm: () => {
      if (!layoutId || !activeChildId) return;
      // The title is left alone: it is the author's header text, and changing the
      // layout underneath no longer reinterprets it.
      updateExperience(
        {
          layoutId,
        },
        {
          experienceId: activeChildId,
          // A layout change doesn't alter events; skip the collection refetch.
          invalidateCollection: false,
          // At parent scope useUpdateExperience's parentExperience is null, so its
          // built-in parent invalidation is a no-op — refresh the parent here so
          // the sidebar's children[].layoutId (thumbnail) stays in sync.
          onSuccess: () =>
            queryClient.invalidateQueries({
              queryKey: createExperienceQueryKey(experience.id),
            }),
        }
      );
      closeChangeLayout();
    },
    cancel: closeChangeLayout,
  };
};
